// Redux Toolkit Query
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
// Redux Toolkit Core
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
// Types
import { MassApprovalAPIResponse, MassApprovalAPIResponseData } from './../../../types/massApproval'
import { MassApprovalRequest } from './timesheetApprovalDetails/timesheetApprovalDetailsSlice'
import { TimesheetStoreState } from '../../timesheetStore'
// API Utilities
import getBaseQueryWithReauth from '../../../utils/api/query-auth'
import getAuth from '../../../utils/api/getToken'

/**
 * Interface representing the state for tracking SignalR progress during mass approval.
 */
export interface MassApprovalProgressState {
	/**
	 * The current progress percentage (0-100).
	 */
	progress: number

	/**
	 * Whether the mass approval process is completed.
	 */
	isCompleted: boolean

	/**
	 * The API response data from the mass approval process.
	 */
	data: MassApprovalAPIResponseData
}

/**
 * Initial state for the SignalR progress tracking slice.
 */
const initialProgressState: MassApprovalProgressState = {
	progress: 0,
	isCompleted: false,
	data: { feedback: { approved: 0, failed: [] }, failedTimesheets: [] },
}

/**
 * Base query configuration for the mass approval API.
 */
const baseQuery = fetchBaseQuery({
	baseUrl: `${process.env.REACT_APP_API_BASE_URL}/Timesheet`,
	timeout: 0,
	prepareHeaders: headers => {
		const token = getAuth()
		if (token) {
			headers.set('authorization', `Bearer ${token.auth}`)
		}
		return headers
	},
})

/**
 * API slice for handling mass approval-related API calls.
 */
export const massApprovalApi = createApi({
	reducerPath: 'massApproval',
	baseQuery: getBaseQueryWithReauth(baseQuery),
	keepUnusedDataFor: 0,
	endpoints: builder => ({
		/**
		 * Mutation for approving a batch of timesheets.
		 *
		 * @param timesheets - The array of timesheets to approve.
		 * @returns The API response or null if no content is returned.
		 */
		approveTimesheetBatch: builder.mutation<MassApprovalAPIResponse | null, MassApprovalRequest[]>({
			query: timesheets => ({
				url: '/approve-timesheet-batch',
				method: 'POST',
				body: timesheets,
			}),
			transformResponse: (
				response: unknown,
				meta: { response?: { status?: number } } | undefined,
			): MassApprovalAPIResponse | null => {
				const status = meta?.response?.status

				// Handle 204 No Content
				if (status === 204) {
					return null
				}

				// Handle 200, 206 with response body
				if (status === 200 || status === 206) {
					return response as MassApprovalAPIResponse
				}

				return response as MassApprovalAPIResponse
			},
			transformErrorResponse: (response: { status: number; data?: unknown }) => {
				if (response.status === 400) {
					return `Bad Request: ${response.data || 'Invalid request'}`
				}
				return `Unexpected status code: ${response.status}`
			},
		}),
	}),
})

/**
 * Slice for managing SignalR progress tracking state.
 */
const massApprovalProgressSlice = createSlice({
	name: 'massApprovalProgress',
	initialState: initialProgressState,
	reducers: {
		/**
		 * Updates the progress percentage.
		 *
		 * @param state - The current state.
		 * @param action - The action containing the new progress value.
		 */
		updateProgress: (state, action: PayloadAction<number>) => {
			state.progress = action.payload
		},

		/**
		 * Resets the progress state to its initial values.
		 *
		 * @param state - The current state.
		 */
		resetProgress: state => {
			state.progress = 0
			state.isCompleted = false
		},

		/**
		 * Marks the progress as complete and sets it to 100%.
		 *
		 * @param state - The current state.
		 */
		setComplete: state => {
			state.isCompleted = true
			state.progress = 100
		},

		/**
		 * Sets the API response data from the mass approval process.
		 *
		 * @param state - The current state.
		 * @param action - The action containing the API response data.
		 */
		setData: (state, action: PayloadAction<MassApprovalAPIResponse | null>) => {
			state.data = action.payload!.data
		},
	},
})

/**
 * Selector to access the mass approval progress state from the Redux store.
 *
 * @param state - The root state of the Redux store.
 * @returns The mass approval progress state.
 */
export const selectMassApprovalProgress = (state: TimesheetStoreState) => state.massApprovalProgress

/**
 * Hook for using the approveTimesheetBatch mutation.
 */
export const { useApproveTimesheetBatchMutation } = massApprovalApi

/**
 * Actions for updating, resetting, and completing the progress state.
 */
export const { updateProgress, resetProgress, setComplete, setData } = massApprovalProgressSlice.actions

/**
 * Reducer for the mass approval progress slice.
 */
export const massApprovalProgressReducer = massApprovalProgressSlice.reducer

/**
 * Export the state values
 */
export const { data, isCompleted, progress } = massApprovalProgressSlice.getInitialState()
